import { useEffect, useState } from "react";
import { getAPAnalyticsOverview, getAPAnalyticsStoreSales, getAPAnalyticsStoreTopSkus } from "../../lib/api";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { 
  TrendingUp, 
  Store, 
  Package,
  DollarSign,
  FileText,
  RefreshCw,
  ChevronRight,
  Calendar
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const DATE_RANGES = {
  'this-month': 'This Month',
  'last-month': 'Last Month',
  'last-quarter': 'Last Quarter',
  'last-year': 'Last Year',
  'all-time': 'All Time',
  'custom': 'Custom Range'
};

function getDateRange(range) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (range) {
    case 'this-month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: today
      };
    case 'last-month':
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0)
      };
    case 'last-quarter':
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
      return {
        start: quarterStart,
        end: new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0)
      };
    case 'last-year':
      return {
        start: new Date(now.getFullYear() - 1, 0, 1),
        end: new Date(now.getFullYear() - 1, 11, 31)
      };
    default:
      return { start: null, end: null };
  }
}

export default function APAnalytics() {
  const [overview, setOverview] = useState(null);
  const [storeSales, setStoreSales] = useState([]);
  const [filteredStoreSales, setFilteredStoreSales] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeSkus, setStoreSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all-time');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    filterByDate();
  }, [dateRange, customStart, customEnd]);

  const loadAnalytics = async () => {
    try {
      const overviewRes = await getAPAnalyticsOverview();
      setOverview(overviewRes.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load analytics");
      setLoading(false);
    }
  };

  const filterByDate = async () => {
    try {
      const range = getDateRange(dateRange);
      const params = {};
      if (dateRange === 'custom') {
        if (customStart) params.from_date = customStart;
        if (customEnd) params.to_date = customEnd;
      } else if (range.start && range.end) {
        params.from_date = range.start.toISOString().split('T')[0];
        params.to_date = range.end.toISOString().split('T')[0];
      }
      const res = await getAPAnalyticsStoreSales(params);
      setStoreSales(res.data);
      setFilteredStoreSales(res.data);
    } catch (error) {
      toast.error("Failed to filter analytics");
    }
  };

  const handleStoreClick = async (storeName) => {
    setSelectedStore(storeName);
    try {
      const response = await getAPAnalyticsStoreTopSkus(storeName);
      setStoreSkus(response.data);
    } catch (error) {
      toast.error("Failed to load store SKUs");
    }
  };

  const filteredTotals = {
    revenue: filteredStoreSales.reduce((sum, s) => sum + (s.total_sales || 0), 0),
    invoices: filteredStoreSales.reduce((sum, s) => sum + (s.invoice_count || 0), 0),
    items: filteredStoreSales.reduce((sum, s) => sum + (s.total_items || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ap-analytics-page">
      {/* Header with Date Filter */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-heading">AP Revenue Analytics</h2>
          <p className="text-slate-600 mt-1">AP brand sales performance and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DATE_RANGES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-36"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-36"
              />
            </div>
          )}
          
          <Button variant="outline" onClick={loadAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold font-mono mt-1">
                  Rs. {filteredTotals.revenue.toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Invoices</p>
                <p className="text-3xl font-bold font-mono mt-1">{filteredTotals.invoices}</p>
              </div>
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Items Sold</p>
                <p className="text-3xl font-bold font-mono mt-1">{filteredTotals.items}</p>
              </div>
              <Package className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Active Stores</p>
                <p className="text-3xl font-bold font-mono mt-1">{filteredStoreSales.length}</p>
              </div>
              <Store className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store-wise Sales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Store className="w-5 h-5" />
              Store-wise Sales
            </CardTitle>
            <CardDescription>Click on a store to view top performing SKUs</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {filteredStoreSales.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Store className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No sales data available</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Store</TableHead>
                      <TableHead className="text-right">Sales</TableHead>
                      <TableHead className="text-right">Invoices</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStoreSales.map((store, idx) => (
                      <TableRow 
                        key={idx}
                        className={`cursor-pointer hover:bg-slate-50 ${selectedStore === store.store ? 'bg-blue-50' : ''}`}
                        onClick={() => handleStoreClick(store.store)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {store.store}
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">Rs. {store.total_sales?.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono">{store.invoice_count}</TableCell>
                        <TableCell className="text-right font-mono">{store.total_items}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top 3 SKUs
              {selectedStore && <Badge variant="secondary" className="ml-2">{selectedStore}</Badge>}
            </CardTitle>
            <CardDescription>
              {selectedStore ? `Best performing products for ${selectedStore}` : 'Select a store to view top SKUs'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedStore ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Select a store from the list</p>
              </div>
            ) : storeSkus.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No SKU data available</p>
              </div>
            ) : (
              <div className="space-y-4">
                {storeSkus.map((sku, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      idx === 0 ? 'bg-amber-50 border-amber-200' :
                      idx === 1 ? 'bg-slate-50 border-slate-200' :
                      'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0 ? 'bg-amber-500 text-white' :
                          idx === 1 ? 'bg-slate-400 text-white' :
                          'bg-orange-400 text-white'
                        }`}>
                          #{idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{sku.sku}</p>
                          <p className="text-sm text-slate-500">{sku.quantity} units sold</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold font-mono text-lg">Rs. {sku.revenue?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">revenue</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Products & Stores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Selling Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overview?.top_products?.map((product, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">#{idx + 1}</Badge>
                    <span className="font-medium text-sm truncate max-w-[200px]" title={product.product}>
                      {product.product}
                    </span>
                  </div>
                  <span className="font-mono font-bold">Rs. {product.revenue?.toLocaleString()}</span>
                </div>
              ))}
              {(!overview?.top_products || overview.top_products.length === 0) && (
                <p className="text-center py-4 text-slate-500">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Revenue Stores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overview?.top_stores?.map((store, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">#{idx + 1}</Badge>
                    <span className="font-medium text-sm truncate max-w-[200px]" title={store.store}>
                      {store.store}
                    </span>
                  </div>
                  <span className="font-mono font-bold">Rs. {store.revenue?.toLocaleString()}</span>
                </div>
              ))}
              {(!overview?.top_stores || overview.top_stores.length === 0) && (
                <p className="text-center py-4 text-slate-500">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
